
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { CustomerArticleDto } from "@/dtos/customerArticleDto";
import { MaterialDto } from "@/dtos/materialDto";
import { ZincCoating } from "@/dtos/zincCoating";
import httpClient from "../../store/httpClient";

@Component
export default class ArticleZincCoatingEditDialog extends Vue {
	@Prop()
	private editZincCoatingDialog!: boolean;
	@Prop()
	private articles!: CustomerArticleDto[];
	private loading: boolean = false;
	private isUpdatingArticleData: boolean = false;
	private isGalvanizePossible: boolean = false;
	private isHotDipPossible: boolean = false;
	private materialCanBeHotDipCoated: boolean = false;
	private failReasons: { articleId: number; failReason: string }[] = [];
	private selectedCoating: number = ZincCoating.None;
	private showHotdipInfo: boolean = false;
	private showGalvanizeInfo: boolean = false;

	@Watch("editZincCoatingDialog")
	private async openTreatmentDialog() {
		if (!this.editZincCoatingDialog) return;
		try {
			this.isGalvanizePossible = false;
			this.isHotDipPossible = false;
			this.materialCanBeHotDipCoated = false;
			this.failReasons = [];
			this.loading = true;
			// Figure out which coatings are available from stored materials...
			let materialIds: number[] = [];
			for (let article of this.articles) {
				if (article.isAssembly) {
					await this.$store.dispatch(
						"loadMissingArticles",
						Array.from(article.subArticleIds)
					);
					const subArticles = this.$store.state.articles.filter((a: CustomerArticleDto) =>
						article.subArticleIds.includes(a.articleId)
					);
					materialIds.push(...subArticles.map((a: CustomerArticleDto) => a.materialId));
				} else {
					materialIds.push(article.materialId);
				}
			}
			let materialSet = new Set(materialIds); // Remove duplicates
			let materials: MaterialDto[] = [];
			materialSet.forEach((a: number) => {
				materials.push(this.$store.getters.getMaterial(a));
			});
			if (materials.every((m) => m.zincCoating === ZincCoating.None)) {
				this.isHotDipPossible = false;
				this.isGalvanizePossible = false;
				return;
			}
			this.isGalvanizePossible = materials.every(
				(m) => m.zincCoating === ZincCoating.Galvanize || m.zincCoating === ZincCoating.Both
			);
			this.materialCanBeHotDipCoated = materials.every(
				(m) => m.zincCoating === ZincCoating.HotDip || m.zincCoating === ZincCoating.Both
			);
			this.isHotDipPossible = this.materialCanBeHotDipCoated && !this.isArticlePowderCoated;

			if (!this.isGalvanizePossible && !this.isHotDipPossible) {
				return;
			}
			// Check for all articles, if they can be zinc coated
			for (let article of this.articles) {
				const response = await httpClient().get(
					`article/IsZincCoatingAllowed?articleId=${article.articleId}`
				);
				if (response.data !== "") {
					this.failReasons.push({
						articleId: article.articleId,
						failReason: response.data,
					});
				}
			}

			if (this.currentZincCoating != ZincCoating.None) {
				if (this.currentZincCoating === ZincCoating.HotDip && this.isHotDipPossible) {
					this.selectedCoating = ZincCoating.HotDip;
				} else if (
					this.currentZincCoating === ZincCoating.Galvanize &&
					this.isGalvanizePossible
				) {
					this.selectedCoating = ZincCoating.Galvanize;
				}
			} else {
				if (this.isHotDipPossible && !this.hasScrewThreadsOrExtrudedThreads) {
					this.selectedCoating = ZincCoating.HotDip;
				} else {
					this.selectedCoating = ZincCoating.Galvanize;
				}
			}
		} catch {
			this.$store.dispatch(
				"setSnackbarErrorText",
				this.$t("dialogs.articleZincCoatingEditDialog.messages.loadError")
			);
		} finally {
			this.loading = false;
		}
	}

	private get hasScrewThreadsOrExtrudedThreads() {
		return this.articles.some((a) =>
			a.featureDtos.some(
				(f) => f.designFormTemplateType === 1 || f.designFormTemplateType === 9
			)
		);
	}

	private get isSingleArticle() {
		return this.articles.length === 1;
	}

	private get isAssembly() {
		return this.articles.some((a) => a.isAssembly);
	}

	private get singleArticle() {
		return this.articles[0];
	}

	private get anyArticlesZincCoated() {
		return this.articles.some((a: CustomerArticleDto) => a.zincCoating > 0);
	}

	private get currentZincCoating(): ZincCoating {
		if (this.articles.every((a) => a.zincCoating === this.articles[0].zincCoating)) {
			// They all have a coating and it's the same
			return this.articles[0].zincCoating;
		} else {
			return ZincCoating.None;
		}
	}

	private get anyArticleCalculating() {
		return this.articles.some((a: CustomerArticleDto) => a.calculating);
	}

	private get hasErrors() {
		return this.failReasons.length > 0;
	}

	private get isSaveButtonDisabled() {
		return (
			this.isUpdatingArticleData ||
			this.hasErrors ||
			(!this.isHotDipPossible && !this.isGalvanizePossible) ||
			this.selectedCoating == 0
		);
	}

	private get isArticlePowderCoated() {
		return this.articles.some((a) => a.powderCoatingTreatment);
	}

	private async saveCoatings() {
		if (this.isUpdatingArticleData) return;
		console.log("saving zinc coating...");
		this.isUpdatingArticleData = true;

		try {
			for (const a of this.articles) {
				if (a.zincCoating === this.selectedCoating) {
					console.log("Skipping the article because the zinc coating stayed the same.");
					continue;
				}

				const data = { articleId: a.articleId, coating: this.selectedCoating };
				try {
					await httpClient().post(
						`article/SetZincCoating?articleId=${data.articleId}&coating=${data.coating}`,
						null
					);
					this.$store.commit("setZincCoatingOfArticle", data);
					this.$store.commit("setArticleStateToCalculating", a.articleId);
				} catch {
					this.$store.dispatch(
						"setSnackbarErrorText",
						this.$t("dialogs.articleZincCoatingEditDialog.messages.saveError", {
							articleId: a.articleId,
						})
					);
				}
			}
			this.$store.dispatch(
				"setSnackbarText",
				this.$t("dialogs.articleZincCoatingEditDialog.messages.saveSuccess")
			);
			this.closeDialog();
		} finally {
			this.isUpdatingArticleData = false;
		}
	}

	private deleteCoatings() {
		console.log("deleting zinc coatings");
		this.selectedCoating = ZincCoating.None;
		this.saveCoatings();
	}

	private closeDialog() {
		this.$emit("closeDialog");
	}
}
